import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { Owner } from '@interfaces-clean/observations.interface';
import { StudyMemberDto } from '@interfaces-clean/study-member.interface';
import { SubjectObservationType } from '@interfaces-clean/subject-monitoring/subject-observations';
import { ObservationDetails } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation-details';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { getUTCDate } from '@utils-clean/date-time-helpers';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import {
    getNamedItemsByType,
    getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem,
    getNamedItemsByTypeVariables,
} from '@queries/generated/common.generated';
import SubjectActionsApi from '@apis/bff-api/subject-monitoring/subject-actions';
import { SubjectMonitoringStudyMemberApi } from '@apis/bff-api/subject-monitoring/subject-monitoring-study-member-api';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';

export interface AddActionForm {
    observationId: string | undefined;
    type: SubjectObservationType;
    requestedDate: Date | null;
    dueDate: Date | null;
    owner: Owner | null;
    responsibleFunction: { ref: string | null; name: string } | null;
    description: string | null;
    comments: string | null;
    resolution: string | null;
    completedDate: Date | null;
    associatedReviewLog: { id: number; datasetDate: string } | null;
}

interface Props {
    observationId: string;
    studyRef: string;
    onSubmit: (id: number) => void;
    onClose: () => void;
    type: SubjectObservationType;
}

const COMPONENT_NAME = 'add-subject-action-dialog';

const AddSubjectActionDialog = ({ observationId, studyRef, onSubmit, onClose, type }: Props) => {
    const { t } = useTranslation('sm');

    const { data: observation, isLoading: loadingObservation } = SubjectObservationsApi.useGetDetails(
        studyRef,
        observationId,
    );
    const { trigger: saveAction } = SubjectActionsApi.useSave(studyRef);

    const [commentsExpanded, setCommentsExpanded] = useState<boolean>(false);
    const [resolutionExpanded, setResolutionExpanded] = useState<boolean>(false);

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<AddActionForm>({
        mode: 'all',
        defaultValues: {
            observationId,
            type,
            requestedDate: getUTCDate(),
            dueDate: null,
            owner: null,
            responsibleFunction: null,
            description: '',
            comments: null,
            resolution: null,
            completedDate: null,
            associatedReviewLog: null,
        },
    });

    const { data: studyMembersData, isLoading: isLoadingStudyMembers } =
        SubjectMonitoringStudyMemberApi.useGetAllStudyMembers(studyRef, true);

    const studyUsers = useMemo(
        () =>
            studyMembersData
                ?.filter((user: StudyMemberDto) => !!user)
                .map((user: StudyMemberDto) => ({
                    fullName: user.name as string,
                    ref: user.ref as string,
                    groupedBy: user.acitve ? 'Active' : 'Inactive',
                    disabled: !user.acitve,
                }))
                .sort((a, b) => {
                    if (a.disabled !== b.disabled) {
                        return a.disabled ? 1 : -1;
                    }
                    return a.fullName.localeCompare(b.fullName);
                }),
        [studyMembersData],
    );

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } = useQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.RESPONSIBLE_FUNCTION,
        },
    });

    const studyFunctions = useMemo(
        () =>
            responsibleFunctionsData?.namedItemsByType
                ?.filter(
                    (ni): ni is getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem => ni?.studyId === studyRef,
                )
                .flatMap((studyFunction) => ({
                    groupedBy: studyFunction.active ? 'Active' : 'Inactive',
                    name: studyFunction.name ?? '',
                    ref: (studyFunction.id as string) ?? '',
                    disabled: !studyFunction.active,
                }))
                .sort((a, b) => {
                    if (a.disabled !== b.disabled) {
                        return a.disabled ? 1 : -1;
                    }
                    return a.name.localeCompare(b.name);
                }),
        [responsibleFunctionsData?.namedItemsByType, studyRef],
    );

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    let dialogTitle = t('common:add');
    switch (type) {
        case SubjectObservationType.Manual:
            dialogTitle += ` ${t('actionDialog.subjectTitle')}`;
            break;
        default:
            dialogTitle = '';
    }

    const primaryBtnLabel = t('common:add');

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    const getDialogHeight = () => {
        switch (type) {
            case SubjectObservationType.Manual:
                return 587.5;
            default:
                return 616.5;
        }
    };

    const getDialogWidth = () => {
        switch (type) {
            case SubjectObservationType.Manual:
                return 612;
            default:
                return 515;
        }
    };

    const onAddAction = async (data: AddActionForm) => {
        try {
            const response = await saveAction({
                observationId: Number(data.observationId),
                ownerRef: data.owner?.ref,
                dueDate: data.dueDate ? stringDateWithoutTimeFromLocal(data.dueDate) : undefined,
                description: data.description,
                comments: data.comments,
                resolution: data.resolution,
                requestedDate: stringDateWithoutTimeFromLocal(data.requestedDate!)!,
                completedDate: data.completedDate ? stringDateWithoutTimeFromLocal(data.completedDate) : undefined,
                studyFunctionRef: data.responsibleFunction?.ref,
                reviewLogId: data.associatedReviewLog?.id,
            });

            if (response?.success) {
                return response.id!;
            }
            return 0;
        } catch {
            return 0;
        }
    };

    const submitAndClose = async (formData: AddActionForm) => {
        const id = await onAddAction(formData);
        onSubmit(id);
    };
    const handlePrimarySubmit = () => handleSubmit((data) => submitAndClose(data))();

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="add-subject-action-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={handlePrimarySubmit}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            initialHeight={getDialogHeight()}
            initialWidth={getDialogWidth()}
            additionalInfo={
                <ObservationDetails
                    observation={observation || undefined}
                    observationLoading={loadingObservation}
                    type={type}
                    area="SM"
                />
            }
            additionalInfoWidth={385}
            additionalInfoBtnLabel={t('observation')}
            bodyPadding="none"
            setHeight="fit-content"
        >
            <div className={cn(style.editSiteObservationDialog, style.rightBorderStyle, 'k-w-full', 'k-h-full')}>
                <div>
                    <form id="add-subject-action" className={cn('k-w-full k-mt-0 k-pl-3 k-pr-3 k-pt-1.5')}>
                        <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', style.formFieldGap)}>
                            <div className="!k-basis-1/2">
                                <DatePickerController
                                    control={control}
                                    name="requestedDate"
                                    data-testid="add-subject-action-requested-date"
                                    fillMode="solid"
                                    required
                                    label={t('actionDialog.requestedDate')}
                                    readonly={isSubmitting}
                                />
                            </div>
                            <div className="!k-basis-1/2">
                                <DatePickerController
                                    control={control}
                                    name="dueDate"
                                    data-testid="add-subject-action-due-date"
                                    fillMode="solid"
                                    label={t('actionDialog.dueDate')}
                                    readonly={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                            <BasicDropDownController
                                label={t('actionDialog.owner')}
                                control={control}
                                name="owner"
                                data-testid="add-subject-action-owner"
                                data={studyUsers ?? []}
                                textField="fullName"
                                groupField="groupedBy"
                                dataItemKey="ref"
                                filterable
                                loading={isLoadingStudyMembers}
                                allowClear
                                readOnly={isSubmitting}
                            />
                        </div>
                        <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                            <BasicDropDownController
                                label={t('actionDialog.responsibleFunction')}
                                control={control}
                                name="responsibleFunction"
                                data-testid="add-subject-action-responsible-function"
                                data={studyFunctions ?? []}
                                textField="name"
                                groupField="groupedBy"
                                dataItemKey="ref"
                                filterable
                                loading={loadingResponsibleFunctions}
                                allowClear
                                readOnly={isSubmitting}
                            />
                        </div>
                        <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                            <TextAreaController
                                className={style.textAreaOverrides}
                                maxLength={3000}
                                control={control}
                                name="description"
                                required
                                data-testid="add-subject-action-description"
                                label={t('actionDialog.description')}
                                rows={4}
                                readOnly={isSubmitting}
                            />
                        </div>
                        <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                            <TextAreaDropdownController
                                maxLength={3000}
                                control={control}
                                className={style.textAreaOverrides}
                                name="comments"
                                data-testid="add-subject-action-field-select-comments"
                                label={t('actionDialog.comments')}
                                rows={4}
                                readOnly={isSubmitting}
                                handleInputChange={async (event) => {
                                    event.value.length > 0 ? setCommentsExpanded(true) : setCommentsExpanded(false);
                                }}
                                expanded={commentsExpanded}
                            />
                        </div>
                        <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                            <TextAreaDropdownController
                                maxLength={3000}
                                control={control}
                                className={style.textAreaOverrides}
                                name="resolution"
                                data-testid="add-subject-action-resolution"
                                label={t('actionDialog.resolution')}
                                rows={4}
                                readOnly={isSubmitting}
                                handleInputChange={async (event) => {
                                    event.value.length > 0 ? setResolutionExpanded(true) : setResolutionExpanded(false);
                                }}
                                expanded={resolutionExpanded}
                            />
                        </div>
                        <div className="k-shrink-0 k-d-flex k-justify-content-left k-pb-md">
                            <div className="!k-basis-1/2">
                                <DatePickerController
                                    control={control}
                                    name="completedDate"
                                    data-testid="add-subject-action-completed-date"
                                    fillMode="solid"
                                    label={t('actionDialog.completedDate')}
                                    className="k-w-full"
                                    readonly={isSubmitting}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </DraggableDialog>
    );
};

export default AddSubjectActionDialog;
